import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, MessageCircle, CircleDot } from "lucide-react";
import { getModelBySlug, DEFAULT_FAQ, MODELS } from "../data/models";
import { SALES, DEALER, SITE, buildWhatsAppLink } from "../data/constants";
import { useSettings, buildWaLink } from "../lib/SettingsContext";
import { trackWhatsAppLead } from "../lib/AnalyticsInjector";
import SEO from "../components/SEO";
import SchemaMarkup from "../components/SchemaMarkup";
import PageSeoOverride from "../components/PageSeoOverride";
import SalesIdentity from "../components/SalesIdentity";
import Handovers from "../components/Handovers";
import DealerLocation from "../components/DealerLocation";
import FAQ from "../components/FAQ";
import { useContent, applyOverride } from "../lib/ContentContext";

export default function ModelDetail() {
  const { slug } = useParams();
  const { overrides, loaded, t } = useContent();
  const { settings } = useSettings();
  const baseModel = getModelBySlug(slug);
  const model = baseModel ? applyOverride(baseModel, overrides[slug]) : null;

  if (!model) return <Navigate to="/models" replace />;

  const waMessage = `Halo ${SALES.name}, saya ingin konsultasi ${model.name}. Mohon dihitungkan simulasi kredit terbaiknya.`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Car",
    name: model.name,
    description: model.shortDesc,
    image: model.hero,
    brand: { "@type": "Brand", name: "BYD" },
    model: model.name,
    vehicleEngine: {
      "@type": "EngineSpecification",
      enginePower: model.specs.Powertrain,
    },
    fuelType: "Electric",
    offers: {
      "@type": "Offer",
      url: `${SITE.domain}/model/${model.slug}`,
      priceCurrency: "IDR",
      price: model.price,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: {
        "@type": "AutoDealer",
        name: DEALER.name,
        address: {
          "@type": "PostalAddress",
          streetAddress: "Jl. Alternatif Cibubur No.41, Jatikarya",
          addressLocality: DEALER.city,
          addressRegion: DEALER.region,
          postalCode: DEALER.postalCode,
          addressCountry: DEALER.country,
        },
        telephone: `+${SALES.phoneIntl}`,
      },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "127",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: DEFAULT_FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title={`${model.name} - ${model.tagline} | Dealer BYD Cibubur`}
        description={`${model.shortDesc} ${model.priceLabel}. Konsultasi langsung dengan ${SALES.name} di ${DEALER.name}. Test drive & hitungan kredit gratis.`}
        canonical={`${SITE.domain}/model/${model.slug}`}
        ogImage={model.hero}
      />
      <SchemaMarkup id={`product-${model.slug}`} schema={productSchema} />
      <SchemaMarkup id={`faq-${model.slug}`} schema={faqSchema} />
      <PageSeoOverride pageKey={`model/${model.slug}`} />

      {/* Hero */}
      <section
        className="relative bg-[#F5F5F7]"
        data-testid={`model-hero-${model.slug}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-10 pb-16 lg:pb-24">
          <div className="flex items-center gap-2 text-xs text-[#6E6E73] mb-6">
            <Link to="/" className="hover:text-[#0F0F0F]">Beranda</Link>
            <span>/</span>
            <Link to="/models" className="hover:text-[#0F0F0F]">Model</Link>
            <span>/</span>
            <span className="text-[#0F0F0F]">{model.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 fade-up">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#F5F5F7] border border-[#E5E5EA] relative">
                {loaded ? (
                  <img
                    key={model.hero}
                    src={model.hero}
                    alt={`${model.name} - ${model.tagline}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[#EAEAEC] animate-pulse" />
                )}
              </div>
            </div>
            <div className="lg:col-span-5 lg:pt-6 fade-up fade-up-delay-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] bg-[#0F0F0F] text-white px-3 py-1.5 rounded-full font-medium">
                  {model.category}
                </span>
                {model.featured && (
                  <span className="text-[10px] uppercase tracking-[0.2em] bg-white border border-[#E5E5EA] text-[#0F0F0F] px-3 py-1.5 rounded-full font-medium">
                    Unit Terlaris
                  </span>
                )}
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#0F0F0F] leading-[1.02]">
                {model.name}
              </h1>
              <p className="mt-3 text-xl font-light italic text-[#6E6E73]">
                {model.tagline}
              </p>
              <p className="mt-6 text-base text-[#6E6E73] leading-relaxed">
                {model.shortDesc}
              </p>

              <div className="mt-8 pt-6 border-t border-[#E5E5EA]">
                <p className="text-xs text-[#86868B]">Harga OTR Jakarta</p>
                <p className="text-3xl font-medium text-[#0F0F0F] mt-1">
                  {model.priceLabel}
                </p>
              </div>

              <div className="mt-4">
                <p className="text-xs text-[#86868B] mb-2">Pilihan Warna</p>
                <div className="flex flex-wrap gap-2">
                  {model.colors.map((c) => (
                    <span
                      key={c}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E5E5EA] bg-white text-[#6E6E73]"
                    >
                      <CircleDot className="w-3 h-3" /> {c}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={buildWaLink(settings, waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppLead(`model-${model.slug}`)}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-3.5 text-sm font-medium transition-colors"
                  data-testid={`model-wa-cta-${model.slug}`}
                >
                  <MessageCircle className="w-4 h-4" />
                  {settings.wa_cta_model || settings.wa_cta_text || "Dapatkan Hitungan Kredit Terbaik"}
                </a>
                <a
                  href={buildWaLink(
                    settings,
                    `Halo ${settings.sales_name}, saya ingin test drive ${model.name}.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppLead(`testdrive-${model.slug}`)}
                  className="inline-flex items-center justify-center rounded-full bg-white hover:bg-[#F5F5F7] text-[#0F0F0F] px-6 py-3.5 text-sm font-medium border border-[#E5E5EA] transition-colors"
                  data-testid={`model-testdrive-cta-${model.slug}`}
                >
                  {settings.wa_cta_testdrive || "Jadwalkan Test Drive"}
                </a>
              </div>
            </div>
          </div>

          {/* Highlight grid */}
          <div className="mt-12 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-5">
            {model.highlights.map((h) => (
              <div
                key={h.label}
                className="rounded-2xl bg-white border border-[#E5E5EA] p-5"
              >
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#86868B]">
                  {h.label}
                </p>
                <p className="mt-2 text-lg lg:text-xl font-medium text-[#0F0F0F]">
                  {h.value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key features — Comfort, Range, Cost Efficiency */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
            Alasan Memilih {model.name}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1]">
            Kenyamanan, daya jelajah, <br className="hidden sm:block" />
            dan <span className="italic font-light">efisiensi maksimal.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {model.keyFeatures.map((f, i) => (
            <article
              key={f.title}
              className="rounded-3xl border border-[#E5E5EA] p-8 hover:border-[#0F0F0F]/40 transition-colors"
              data-testid={`feature-${model.slug}-${i}`}
            >
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0F0F0F] text-white text-sm font-medium">
                0{i + 1}
              </span>
              <h3 className="mt-6 text-xl font-medium text-[#0F0F0F]">
                {f.title}
              </h3>
              <p className="mt-3 text-sm text-[#6E6E73] leading-relaxed">
                {f.desc}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Specs table */}
      <section className="bg-[#F5F5F7] border-y border-[#E5E5EA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
                Spesifikasi Teknis
              </p>
              <h2 className="text-3xl lg:text-4xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1]">
                Detail performa <br />
                <span className="italic font-light">{model.name}.</span>
              </h2>
              <p className="mt-5 text-sm text-[#6E6E73] leading-relaxed">
                Data teknis lengkap unit baru. Spesifikasi dapat berubah sesuai
                varian yang dipilih.
              </p>
            </div>
            <div className="lg:col-span-8">
              <dl className="divide-y divide-[#E5E5EA] border-t border-b border-[#E5E5EA]">
                {Object.entries(model.specs).map(([k, v]) => (
                  <div
                    key={k}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-5"
                  >
                    <dt className="text-sm text-[#86868B]">{k}</dt>
                    <dd className="sm:col-span-2 text-sm text-[#0F0F0F] font-medium">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      <SalesIdentity customMessage={waMessage} />
      <Handovers />
      <FAQ
        title={t("faq.model_title_template", "FAQ tentang {name}").replace("{name}", model.name)}
        subtitle={t("faq.model_subtitle", "Pertanyaan umum seputar charging di rumah, garansi Blade Battery, dan biaya operasional harian.")}
      />
      <DealerLocation />

      {/* Related models */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <p className="text-xs uppercase tracking-[0.25em] text-[#6E6E73] mb-5">
          Model Lainnya
        </p>
        <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0F0F0F] leading-[1.1] mb-10">
          Bandingkan dengan unit lain.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MODELS.filter((m) => m.slug !== model.slug)
            .slice(0, 3)
            .map((base) => {
              const m = applyOverride(base, overrides[base.slug]);
              return (
              <Link
                key={m.slug}
                to={`/model/${m.slug}`}
                className="group rounded-3xl border border-[#E5E5EA] bg-[#F5F5F7] overflow-hidden hover:border-[#0F0F0F]/30 transition-colors"
              >
                <div className="aspect-[4/3] bg-[#F5F5F7] overflow-hidden relative">
                  {loaded ? (
                    <img
                      key={m.thumb}
                      src={m.thumb}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[#EAEAEC] animate-pulse" />
                  )}
                </div>
                <div className="p-6 flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-[#0F0F0F]">
                      {m.name}
                    </h3>
                    <p className="text-xs text-[#86868B] mt-1">
                      {m.priceLabel}
                    </p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#0F0F0F] shrink-0 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </Link>
              );
            })}
        </div>
      </section>
    </>
  );
}
